export function buildRoadGraph(overpassData) {
  const nodes = new Map();
  const edges = new Map();
  let edgeCount = 0;

  for (const way of overpassData.elements || []) {
    if (way.type !== "way" || !way.nodes || !way.geometry || way.nodes.length !== way.geometry.length) {
      continue;
    }

    for (let index = 0; index < way.nodes.length; index += 1) {
      const id = String(way.nodes[index]);
      if (!nodes.has(id)) {
        const point = way.geometry[index];
        nodes.set(id, {
          id,
          coordinate: [point.lon, point.lat],
          neighbors: [],
        });
      }
    }

    for (let index = 1; index < way.nodes.length; index += 1) {
      const from = nodes.get(String(way.nodes[index - 1]));
      const to = nodes.get(String(way.nodes[index]));
      if (!from || !to || from.id === to.id) continue;
      const distanceMeters = distanceInMeters(from.coordinate, to.coordinate);
      const edgeId = edgeKey(from.id, to.id);
      const edge = {
        id: edgeId,
        fromId: from.id,
        toId: to.id,
        distanceMeters,
        wayId: way.id,
        roadType: way.tags?.highway || "unknown",
        // Kept so a stretch of road can be named rather than described. Held
        // ground reads as somewhere ("Butternut Drive") instead of as a shape.
        name: way.tags?.name || null,
      };
      edges.set(edgeId, edge);
      from.neighbors.push({ id: to.id, distanceMeters, wayId: way.id, edgeId });
      to.neighbors.push({ id: from.id, distanceMeters, wayId: way.id, edgeId });
      edgeCount += 1;
    }
  }

  return { nodes, edges, edgeCount };
}

export function buildTacticalCorridors(graph) {
  const corridors = new Map();
  const corridorByEdge = new Map();
  const visited = new Set();
  const junctionIds = new Set(
    [...graph.nodes.values()]
      .filter((node) => node.neighbors.length !== 2)
      .map((node) => node.id),
  );

  for (const startId of junctionIds) {
    const start = graph.nodes.get(startId);
    for (const neighbor of start.neighbors) {
      if (visited.has(neighbor.edgeId)) continue;
      const nodeIds = [startId];
      const edgeIds = [];
      let previousId = startId;
      let currentId = neighbor.id;

      while (true) {
        const edgeId = edgeKey(previousId, currentId);
        visited.add(edgeId);
        edgeIds.push(edgeId);
        nodeIds.push(currentId);
        if (junctionIds.has(currentId)) break;
        const next = graph.nodes.get(currentId).neighbors.find((candidate) => candidate.id !== previousId);
        if (!next || visited.has(next.edgeId)) break;
        previousId = currentId;
        currentId = next.id;
      }

      addCorridor(graph, corridors, corridorByEdge, nodeIds, edgeIds);
    }
  }

  for (const edge of graph.edges.values()) {
    if (visited.has(edge.id)) continue;
    addCorridor(graph, corridors, corridorByEdge, [edge.fromId, edge.toId], [edge.id]);
  }

  for (const corridor of corridors.values()) {
    corridor.neighborIds = [...corridors.values()]
      .filter((candidate) => candidate.id !== corridor.id
        && (candidate.startNodeId === corridor.startNodeId
          || candidate.startNodeId === corridor.endNodeId
          || candidate.endNodeId === corridor.startNodeId
          || candidate.endNodeId === corridor.endNodeId))
      .map((candidate) => candidate.id);
  }

  return { corridors, corridorByEdge };
}

export function corridorIdsForNodePath(tacticalGraph, nodeIds) {
  const corridorIds = [];
  for (let index = 1; index < nodeIds.length; index += 1) {
    const corridorId = tacticalGraph.corridorByEdge.get(edgeKey(nodeIds[index - 1], nodeIds[index]));
    if (corridorId && corridorIds.at(-1) !== corridorId) corridorIds.push(corridorId);
  }
  return corridorIds;
}

export function corridorDistance(tacticalGraph, fromId, toId, maxDistance = Infinity) {
  if (fromId === toId) return 0;
  const queue = [{ id: fromId, distance: 0 }];
  const visited = new Set([fromId]);
  while (queue.length) {
    const current = queue.shift();
    if (current.distance >= maxDistance) continue;
    for (const neighborId of tacticalGraph.corridors.get(current.id)?.neighborIds || []) {
      if (neighborId === toId) return current.distance + 1;
      if (visited.has(neighborId)) continue;
      visited.add(neighborId);
      queue.push({ id: neighborId, distance: current.distance + 1 });
    }
  }
  return Infinity;
}

// BFS outward from a seed corridor, accumulating each hop's own road length,
// to find every corridor within `radiusMeters` of road distance. Territory
// size is defined by road distance, not edge count, because OSM edges vary
// from 20m to 500m — an edge-count radius would be wildly inconsistent
// across neighborhoods.
export function corridorsWithinRoadDistance(tacticalGraph, seedCorridorId, radiusMeters) {
  if (!tacticalGraph.corridors.has(seedCorridorId)) return [];
  const bestDistance = new Map([[seedCorridorId, 0]]);
  const queue = [seedCorridorId];
  while (queue.length) {
    const currentId = queue.shift();
    const currentDistance = bestDistance.get(currentId);
    const corridor = tacticalGraph.corridors.get(currentId);
    for (const neighborId of corridor?.neighborIds || []) {
      const neighbor = tacticalGraph.corridors.get(neighborId);
      if (!neighbor) continue;
      const distanceMeters = currentDistance + neighbor.lengthMeters;
      if (distanceMeters > radiusMeters) continue;
      if ((bestDistance.get(neighborId) ?? Infinity) <= distanceMeters) continue;
      bestDistance.set(neighborId, distanceMeters);
      queue.push(neighborId);
    }
  }
  return [...bestDistance.keys()];
}

export function nearestCorridor(tacticalGraph, coordinate, maxDistanceMeters = Infinity) {
  let nearest = null;
  for (const corridor of tacticalGraph.corridors.values()) {
    const projection = nearestPointOnPolyline(coordinate, corridor.coordinates);
    if (!projection) continue;
    if (!nearest || projection.distanceMeters < nearest.distanceMeters) {
      nearest = { corridor, ...projection };
    }
  }
  return nearest && nearest.distanceMeters <= maxDistanceMeters ? nearest : null;
}

// Every corridor whose centreline runs within `maxDistanceMeters` of the point,
// nearest first. This is the "which corridors does this building belong to"
// query for the single-D vision model: a building usually belongs to one
// corridor, but near a junction it belongs to each corridor whose band reaches
// it — and revealing any of them reveals the building, matching the fog band
// exactly. Empty when the point is farther than the cutoff from every corridor
// (an orphan building that never reveals).
export function corridorsNearCoordinate(tacticalGraph, coordinate, maxDistanceMeters) {
  const hits = [];
  for (const corridor of tacticalGraph.corridors.values()) {
    const projection = nearestPointOnPolyline(coordinate, corridor.coordinates);
    if (projection && projection.distanceMeters <= maxDistanceMeters) {
      hits.push({ corridorId: corridor.id, distanceMeters: projection.distanceMeters });
    }
  }
  hits.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return hits.map((hit) => hit.corridorId);
}

// The corridors a stationary watcher/base/outpost sees, by graph topology
// rather than a metric flood (plans/CORRIDOR_VISION_REDESIGN_PLAN.md). The
// placement resolves to a junction anchor (within junctionSnapMeters of a real
// intersection node) or a mid-corridor anchor, and the watched set follows:
//   watcher, junction -> every corridor at the node (breadth)
//   watcher, mid      -> the anchor corridor + the straightest continuation off each end (depth)
//   base,    junction -> every corridor at the node + one straight hop off each (breadth + depth)
//   base,    mid      -> the anchor corridor + every corridor at both ends (full breadth)
// `extended` = true selects the base/outpost merge; false is the watcher rule.
// Returns [] when the coordinate can't snap to a corridor at all.
export function corridorsWatched(tacticalGraph, coordinate, { junctionSnapMeters = 50, extended = false } = {}) {
  const hit = nearestCorridor(tacticalGraph, coordinate);
  if (!hit) return [];
  const anchor = hit.corridor;
  const watched = new Set();
  const junctionNodeId = junctionAnchorNode(anchor, coordinate, junctionSnapMeters);

  if (junctionNodeId != null) {
    for (const corridor of corridorsTouchingNode(tacticalGraph, junctionNodeId)) {
      watched.add(corridor.id);
      if (extended) {
        const cont = straightestContinuation(tacticalGraph, corridor, otherCorridorNode(corridor, junctionNodeId));
        if (cont) watched.add(cont);
      }
    }
  } else {
    // Mid-corridor anchor. A plain watcher/base sees just the anchor corridor —
    // its D-band already grazes the corridors meeting at each end (enough to spot
    // movement), and keeping mid-corridor tight makes a junction placement
    // (breadth) the meaningfully stronger choice. The extended (outpost) watch
    // still fans out to the full breadth at both ends.
    watched.add(anchor.id);
    if (extended) {
      for (const nodeId of [anchor.startNodeId, anchor.endNodeId]) {
        for (const corridor of corridorsTouchingNode(tacticalGraph, nodeId)) watched.add(corridor.id);
      }
    }
  }
  return [...watched];
}

// The corridor's end node the coordinate is anchored to, if it's within
// snapMeters of one (nearer end wins) — a junction anchor. Null means the
// placement is mid-corridor. A dead-end node resolves gracefully: "every
// corridor at the node" is just the one corridor, and a straight continuation
// off it is null.
function junctionAnchorNode(corridor, coordinate, snapMeters) {
  const coords = corridor.coordinates;
  const start = coords[0];
  const end = coords[coords.length - 1];
  const dStart = distanceInMeters(coordinate, start);
  const dEnd = distanceInMeters(coordinate, end);
  if (dStart <= snapMeters && dStart <= dEnd) return corridor.startNodeId;
  if (dEnd <= snapMeters) return corridor.endNodeId;
  return null;
}

// Of the corridors leaving `throughNodeId`, the one that best continues
// `corridor`'s heading through that node — "keep going straight down the
// street." Null at a dead end. Tie-break on lowest corridor ID for determinism.
function straightestContinuation(tacticalGraph, corridor, throughNodeId) {
  if (throughNodeId == null) return null;
  const approach = corridorApproachDir(corridor, throughNodeId);
  if (!approach) return null;
  let best = null;
  let bestDot = -Infinity;
  for (const other of corridorsTouchingNode(tacticalGraph, throughNodeId)) {
    if (other.id === corridor.id) continue;
    const depart = corridorDepartDir(other, throughNodeId);
    if (!depart) continue;
    const dot = approach[0] * depart[0] + approach[1] * depart[1];
    if (dot > bestDot || (dot === bestDot && (best === null || other.id < best))) {
      bestDot = dot;
      best = other.id;
    }
  }
  return best;
}

// Unit travel direction of `corridor` as it arrives at `node` (pointing into
// the node); and as it departs `node` (pointing away). A straight continuation
// has approach ≈ depart (dot ≈ 1).
function corridorApproachDir(corridor, node) {
  const c = corridor.coordinates;
  if (corridor.endNodeId === node) return unitDirection(c[c.length - 2], c[c.length - 1]);
  if (corridor.startNodeId === node) return unitDirection(c[1], c[0]);
  return null;
}

function corridorDepartDir(corridor, node) {
  const c = corridor.coordinates;
  if (corridor.startNodeId === node) return unitDirection(c[0], c[1]);
  if (corridor.endNodeId === node) return unitDirection(c[c.length - 1], c[c.length - 2]);
  return null;
}

function unitDirection(from, to) {
  if (!from || !to) return null;
  const latRad = (from[1] + to[1]) / 2 * Math.PI / 180;
  const dx = (to[0] - from[0]) * Math.cos(latRad);
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  return length ? [dx / length, dy / length] : null;
}

export function intersectionPatrolRoute(tacticalGraph, anchorNodeId, excludedCorridorId, options = {}) {
  const loop = findIntersectionLoop(tacticalGraph, anchorNodeId, excludedCorridorId, options);
  if (loop) return loop;
  return findIntersectionOutAndBack(tacticalGraph, anchorNodeId, excludedCorridorId, options);
}

export function findIntersectionLoop(tacticalGraph, anchorNodeId, excludedCorridorId, options = {}) {
  const {
    minDistanceMeters = 120,
    maxDistanceMeters = 450,
    idealDistanceMeters = 250,
    maxCorridors = 5,
    baseCoordinate = null,
    maxDistanceFromBaseMeters = Infinity,
    avoidNodeIds = [],
  } = options;
  const avoidedNodes = new Set(avoidNodeIds.map(String));
  const queue = [{ nodeId: anchorNodeId, corridorIds: [], nodeIds: [anchorNodeId], distanceMeters: 0 }];
  const loops = [];
  while (queue.length) {
    const state = queue.shift();
    if (state.corridorIds.length >= maxCorridors) continue;
    for (const corridor of corridorsTouchingNode(tacticalGraph, state.nodeId)) {
      if (corridor.id === excludedCorridorId || state.corridorIds.includes(corridor.id)) continue;
      const nextNodeId = otherCorridorNode(corridor, state.nodeId);
      if (!nextNodeId) continue;
      if (nextNodeId !== anchorNodeId && avoidedNodes.has(nextNodeId)) continue;
      const distanceMeters = state.distanceMeters + corridor.lengthMeters;
      if (distanceMeters > maxDistanceMeters) continue;
      const corridorIds = [...state.corridorIds, corridor.id];
      const nodeIds = [...state.nodeIds, nextNodeId];
      const coordinates = routeCoordinatesForCorridors(tacticalGraph, anchorNodeId, corridorIds);
      if (!coordinates || routeExceedsBaseDistance(coordinates, baseCoordinate, maxDistanceFromBaseMeters)) continue;
      const closesLoop = nextNodeId === anchorNodeId && (corridorIds.length >= 2 || corridor.startNodeId === corridor.endNodeId);
      if (closesLoop && distanceMeters >= minDistanceMeters) {
        loops.push({
          mode: "loop",
          anchorNodeId,
          excludedCorridorId,
          corridorIds,
          nodeIds,
          coordinates,
          distanceMeters,
        });
      } else {
        queue.push({ nodeId: nextNodeId, corridorIds, nodeIds, distanceMeters });
      }
    }
  }
  return loops.sort((a, b) => (
    Math.abs(a.distanceMeters - idealDistanceMeters) - Math.abs(b.distanceMeters - idealDistanceMeters)
      || a.corridorIds.length - b.corridorIds.length
      || a.corridorIds.join(",").localeCompare(b.corridorIds.join(","))
  ))[0] || null;
}

export function findIntersectionOutAndBack(tacticalGraph, anchorNodeId, excludedCorridorId, options = {}) {
  const {
    minDistanceMeters = 120,
    maxDistanceMeters = 320,
    idealDistanceMeters = 220,
    maxCorridors = 3,
    baseCoordinate = null,
    maxDistanceFromBaseMeters = Infinity,
    avoidNodeIds = [],
  } = options;
  const avoidedNodes = new Set(avoidNodeIds.map(String));
  const queue = [{ nodeId: anchorNodeId, corridorIds: [], nodeIds: [anchorNodeId], distanceMeters: 0 }];
  const arms = [];
  while (queue.length) {
    const state = queue.shift();
    if (state.corridorIds.length >= maxCorridors) continue;
    for (const corridor of corridorsTouchingNode(tacticalGraph, state.nodeId)) {
      if (corridor.id === excludedCorridorId || state.corridorIds.includes(corridor.id)) continue;
      const nextNodeId = otherCorridorNode(corridor, state.nodeId);
      if (!nextNodeId || nextNodeId === anchorNodeId) continue;
      if (avoidedNodes.has(nextNodeId)) continue;
      const distanceMeters = state.distanceMeters + corridor.lengthMeters;
      if (distanceMeters > maxDistanceMeters) continue;
      const corridorIds = [...state.corridorIds, corridor.id];
      const nodeIds = [...state.nodeIds, nextNodeId];
      const coordinates = routeCoordinatesForCorridors(tacticalGraph, anchorNodeId, corridorIds);
      if (!coordinates || routeExceedsBaseDistance(coordinates, baseCoordinate, maxDistanceFromBaseMeters)) continue;
      arms.push({
        corridorIds,
        nodeIds,
        coordinates,
        distanceMeters,
        firstCorridorId: corridorIds[0],
      });
      queue.push({ nodeId: nextNodeId, corridorIds, nodeIds, distanceMeters });
    }
  }
  const paths = twoSidedOutAndBackRoutes(arms, {
    anchorNodeId,
    excludedCorridorId,
    minDistanceMeters,
    maxDistanceMeters,
    idealDistanceMeters,
    maxCorridors,
  });
  if (paths.length) return sortOutAndBackRoutes(paths, idealDistanceMeters)[0];

  for (const arm of arms) {
    if (arm.distanceMeters < minDistanceMeters) continue;
    paths.push({
      mode: "out-and-back",
      anchorNodeId,
      excludedCorridorId,
      corridorIds: arm.corridorIds,
      nodeIds: arm.nodeIds,
      coordinates: arm.coordinates,
      distanceMeters: arm.distanceMeters,
      anchorOffsetMeters: 0,
    });
  }
  return sortOutAndBackRoutes(paths, idealDistanceMeters)[0] || null;
}

function twoSidedOutAndBackRoutes(arms, options) {
  const {
    anchorNodeId,
    excludedCorridorId,
    minDistanceMeters,
    maxDistanceMeters,
    maxCorridors,
  } = options;
  const paths = [];
  for (let leftIndex = 0; leftIndex < arms.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < arms.length; rightIndex += 1) {
      const left = arms[leftIndex];
      const right = arms[rightIndex];
      if (left.firstCorridorId === right.firstCorridorId) continue;
      if (left.corridorIds.length + right.corridorIds.length > maxCorridors) continue;
      if (routesShareInteriorNode(left.nodeIds, right.nodeIds)) continue;
      if (routesShareCorridor(left.corridorIds, right.corridorIds)) continue;
      const distanceMeters = left.distanceMeters + right.distanceMeters;
      if (distanceMeters < minDistanceMeters || distanceMeters > maxDistanceMeters) continue;
      paths.push({
        mode: "out-and-back",
        anchorNodeId,
        excludedCorridorId,
        corridorIds: [...left.corridorIds].reverse().concat(right.corridorIds),
        nodeIds: [...left.nodeIds].reverse().concat(right.nodeIds.slice(1)),
        coordinates: [...left.coordinates].reverse().concat(right.coordinates.slice(1)),
        distanceMeters,
        anchorOffsetMeters: left.distanceMeters,
      });
    }
  }
  return paths;
}

function routesShareInteriorNode(leftNodeIds, rightNodeIds) {
  const leftInterior = new Set(leftNodeIds.slice(1));
  return rightNodeIds.slice(1).some((nodeId) => leftInterior.has(nodeId));
}

function routesShareCorridor(leftCorridorIds, rightCorridorIds) {
  const left = new Set(leftCorridorIds);
  return rightCorridorIds.some((corridorId) => left.has(corridorId));
}

function sortOutAndBackRoutes(paths, idealDistanceMeters) {
  return paths.sort((a, b) => (
    Math.abs(a.distanceMeters - idealDistanceMeters) - Math.abs(b.distanceMeters - idealDistanceMeters)
      || Math.abs((a.anchorOffsetMeters ?? 0) - (a.distanceMeters / 2)) - Math.abs((b.anchorOffsetMeters ?? 0) - (b.distanceMeters / 2))
      || a.corridorIds.length - b.corridorIds.length
      || a.corridorIds.join(",").localeCompare(b.corridorIds.join(","))
  ));
}

export function nearestNode(graph, coordinate, maxDistanceMeters = Infinity) {
  let nearest = null;
  for (const node of graph.nodes.values()) {
    const distanceMeters = distanceInMeters(coordinate, node.coordinate);
    if (!nearest || distanceMeters < nearest.distanceMeters) {
      nearest = { node, distanceMeters };
    }
  }
  return nearest && nearest.distanceMeters <= maxDistanceMeters ? nearest : null;
}

export function nearestRouteProgress(point, routeCoordinates) {
  if (!Array.isArray(routeCoordinates) || routeCoordinates.length < 2) {
    return { progress: 0, distanceMeters: Infinity };
  }
  const segmentLengths = routeCoordinates.slice(1).map((coordinate, index) => (
    distanceInMeters(routeCoordinates[index], coordinate)
  ));
  const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
  let traversed = 0;
  let nearest = { progress: 0, distanceMeters: Infinity };
  for (let index = 0; index < segmentLengths.length; index += 1) {
    const projection = projectPointToSegment(point, routeCoordinates[index], routeCoordinates[index + 1]);
    if (projection.distanceMeters < nearest.distanceMeters) {
      nearest = {
        distanceMeters: projection.distanceMeters,
        progress: totalLength ? (traversed + segmentLengths[index] * projection.fraction) / totalLength : 0,
      };
    }
    traversed += segmentLengths[index];
  }
  return nearest;
}

// Snaps a coordinate to the nearest point *along* a road edge (not just the
// nearest graph node), so a marker glides continuously along the street.
export function nearestPointOnRoads(graph, coordinate, maxDistanceMeters = Infinity) {
  if (!graph?.edges) return null;
  let nearest = null;
  for (const edge of graph.edges.values()) {
    const from = graph.nodes.get(edge.fromId)?.coordinate;
    const to = graph.nodes.get(edge.toId)?.coordinate;
    if (!from || !to) continue;
    const projection = projectPointToSegment(coordinate, from, to);
    if (!nearest || projection.distanceMeters < nearest.distanceMeters) {
      nearest = {
        ...projection,
        edgeId: edge.id,
        fromId: edge.fromId,
        toId: edge.toId,
        offsetFromMeters: distanceInMeters(from, projection.coordinate),
        offsetToMeters: distanceInMeters(to, projection.coordinate),
      };
    }
  }
  return nearest && nearest.distanceMeters <= maxDistanceMeters ? nearest : null;
}

// Virtual-edge routing: instead of snapping each endpoint to its single
// nearest NODE (which can start a walk in the wrong direction — or worse,
// send A* the long way around a block without ever re-crossing the origin),
// snap to the nearest point ON an edge and try both of that edge's endpoint
// nodes, weighted by the along-edge offset. The cheapest total wins, so
// "which way do I set off" is decided by real cost.
export function findRouteBetweenPoints(graph, origin, destination, maxSnapDistanceMeters = 250, options = {}) {
  const snapO = nearestPointOnRoads(graph, origin, maxSnapDistanceMeters);
  const snapD = nearestPointOnRoads(graph, destination, maxSnapDistanceMeters);
  if (!snapO || !snapD) {
    return findRoute(graph, origin, destination, maxSnapDistanceMeters, options);
  }

  // Same edge: walk along it directly.
  if (snapO.edgeId === snapD.edgeId) {
    const coordinates = removeAdjacentDuplicates([origin, snapO.coordinate, snapD.coordinate, destination]);
    return {
      reachable: true,
      coordinates,
      nodeIds: [],
      distanceMeters: polylineDistance(coordinates),
      startSnapMeters: snapO.distanceMeters,
      endSnapMeters: snapD.distanceMeters,
    };
  }

  const originCandidates = [
    { nodeId: snapO.fromId, offset: snapO.offsetFromMeters },
    { nodeId: snapO.toId, offset: snapO.offsetToMeters },
  ];
  const destinationCandidates = [
    { nodeId: snapD.fromId, offset: snapD.offsetFromMeters },
    { nodeId: snapD.toId, offset: snapD.offsetToMeters },
  ];

  let best = null;
  for (const from of originCandidates) {
    for (const to of destinationCandidates) {
      const nodeIds = from.nodeId === to.nodeId ? [from.nodeId] : aStar(graph, from.nodeId, to.nodeId, options);
      if (!nodeIds) continue;
      const nodeCoordinates = nodeIds.map((id) => graph.nodes.get(id).coordinate);
      const pathMeters = polylineDistance(nodeCoordinates);
      const total = from.offset + pathMeters + to.offset;
      if (!best || total < best.total) {
        best = { total, nodeIds, nodeCoordinates };
      }
    }
  }
  if (!best) {
    return {
      reachable: false,
      reason: "No known road connects these points.",
      startSnapMeters: snapO.distanceMeters,
      endSnapMeters: snapD.distanceMeters,
    };
  }

  const coordinates = removeAdjacentDuplicates([
    origin,
    snapO.coordinate,
    ...best.nodeCoordinates,
    snapD.coordinate,
    destination,
  ]);
  return {
    reachable: true,
    coordinates,
    nodeIds: best.nodeIds,
    distanceMeters: polylineDistance(coordinates),
    startSnapMeters: snapO.distanceMeters,
    endSnapMeters: snapD.distanceMeters,
  };
}

export function nearestPointOnPolyline(point, coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  let nearest = null;
  for (let index = 1; index < coordinates.length; index += 1) {
    const projection = projectPointToSegment(point, coordinates[index - 1], coordinates[index]);
    if (!nearest || projection.distanceMeters < nearest.distanceMeters) nearest = projection;
  }
  return nearest;
}

export function projectPointToSegment(point, start, end) {
  const latitude = (point[1] + start[1] + end[1]) / 3 * Math.PI / 180;
  const metersPerLongitude = Math.cos(latitude) * Math.PI * 6371000 / 180;
  const metersPerLatitude = Math.PI * 6371000 / 180;
  const toLocal = (coordinate) => [
    (coordinate[0] - start[0]) * metersPerLongitude,
    (coordinate[1] - start[1]) * metersPerLatitude,
  ];
  const localPoint = toLocal(point);
  const localEnd = toLocal(end);
  const denominator = localEnd[0] ** 2 + localEnd[1] ** 2;
  const fraction = denominator
    ? Math.max(0, Math.min(1, (localPoint[0] * localEnd[0] + localPoint[1] * localEnd[1]) / denominator))
    : 0;
  const projected = [
    start[0] + (end[0] - start[0]) * fraction,
    start[1] + (end[1] - start[1]) * fraction,
  ];
  return {
    coordinate: projected,
    distanceMeters: distanceInMeters(point, projected),
    fraction,
  };
}

function corridorsTouchingNode(tacticalGraph, nodeId) {
  return [...tacticalGraph.corridors.values()].filter((corridor) => (
    corridor.startNodeId === nodeId || corridor.endNodeId === nodeId
  ));
}

function otherCorridorNode(corridor, nodeId) {
  if (corridor.startNodeId === nodeId) return corridor.endNodeId;
  if (corridor.endNodeId === nodeId) return corridor.startNodeId;
  return null;
}

function routeCoordinatesForCorridors(tacticalGraph, anchorNodeId, corridorIds) {
  let currentNodeId = anchorNodeId;
  const coordinates = [];
  for (const corridorId of corridorIds) {
    const corridor = tacticalGraph.corridors.get(corridorId);
    if (!corridor) return null;
    const forward = corridor.startNodeId === currentNodeId;
    const reverse = corridor.endNodeId === currentNodeId;
    if (!forward && !reverse) return null;
    const segment = forward ? corridor.coordinates : [...corridor.coordinates].reverse();
    coordinates.push(...(coordinates.length ? segment.slice(1) : segment));
    currentNodeId = forward ? corridor.endNodeId : corridor.startNodeId;
  }
  return removeAdjacentDuplicates(coordinates);
}

function routeExceedsBaseDistance(coordinates, baseCoordinate, maxDistanceFromBaseMeters) {
  if (!baseCoordinate || !Number.isFinite(maxDistanceFromBaseMeters)) return false;
  return coordinates.some((coordinate) => distanceInMeters(coordinate, baseCoordinate) > maxDistanceFromBaseMeters);
}

export function roadblockSlotsForCorridor(corridor, {
  offsetMeters = 10,
  minimumLengthMeters = 25,
} = {}) {
  if (!corridor || corridor.lengthMeters < minimumLengthMeters || corridor.coordinates.length < 2) return [];
  return [
    roadblockSlotForCorridorEnd(corridor, corridor.startNodeId, offsetMeters),
    roadblockSlotForCorridorEnd(corridor, corridor.endNodeId, offsetMeters),
  ].filter(Boolean);
}

export function blockedEdgeIdsForApproaches(tacticalGraph, blockedApproaches) {
  const blocked = new Set();
  for (const approach of blockedApproaches || []) {
    const corridorId = typeof approach === "string" ? approach.split("|")[0] : approach.corridorId;
    const endNodeId = typeof approach === "string" ? approach.split("|")[1] : approach.endNodeId;
    const corridor = tacticalGraph?.corridors?.get(corridorId);
    if (!corridor || !endNodeId) continue;
    const edgeId = endNodeId === corridor.startNodeId
      ? corridor.edgeIds[0]
      : endNodeId === corridor.endNodeId
        ? corridor.edgeIds.at(-1)
        : null;
    if (edgeId) blocked.add(edgeId);
  }
  return blocked;
}

export function findRoute(graph, origin, destination, maxSnapDistanceMeters = 250, options = {}) {
  const start = nearestNode(graph, origin, maxSnapDistanceMeters);
  const end = nearestNode(graph, destination, maxSnapDistanceMeters);
  if (!start || !end) {
    return {
      reachable: false,
      reason: !start ? "Base is too far from a known road." : "Destination is too far from a known road.",
    };
  }

  const nodeIds = aStar(graph, start.node.id, end.node.id, options);
  if (!nodeIds) {
    return {
      reachable: false,
      reason: "No known road connects the base to this destination.",
      startSnapMeters: start.distanceMeters,
      endSnapMeters: end.distanceMeters,
    };
  }

  const roadCoordinates = nodeIds.map((id) => graph.nodes.get(id).coordinate);
  const coordinates = removeAdjacentDuplicates([origin, ...roadCoordinates, destination]);
  return {
    reachable: true,
    coordinates,
    nodeIds,
    distanceMeters: polylineDistance(coordinates),
    startSnapMeters: start.distanceMeters,
    endSnapMeters: end.distanceMeters,
  };
}

export function findRouteThrough(graph, coordinates, maxSnapDistanceMeters = 250, options = {}) {
  if (coordinates.length < 2) {
    return { reachable: false, reason: "A route requires an origin and destination." };
  }

  const legs = [];
  const routeCoordinates = [];
  const routeNodeIds = [];
  let distanceMeters = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const leg = findRouteBetweenPoints(
      graph,
      coordinates[index - 1],
      coordinates[index],
      maxSnapDistanceMeters,
      options,
    );
    if (!leg.reachable) {
      return {
        reachable: false,
        reason: `Route leg ${index} is unreachable. ${leg.reason}`,
        failedLeg: index,
      };
    }
    legs.push(leg);
    distanceMeters += leg.distanceMeters;
    routeCoordinates.push(...(index === 1 ? leg.coordinates : leg.coordinates.slice(1)));
    routeNodeIds.push(...(index === 1 ? leg.nodeIds : leg.nodeIds.slice(1)));
  }

  return {
    reachable: true,
    coordinates: removeAdjacentDuplicates(routeCoordinates),
    nodeIds: removeAdjacentValues(routeNodeIds),
    distanceMeters,
    legs,
  };
}

export function coordinateAlongRoute(coordinates, progress) {
  if (!coordinates.length) return null;
  if (coordinates.length === 1 || progress <= 0) return coordinates[0];
  if (progress >= 1) return coordinates[coordinates.length - 1];

  const segments = [];
  let totalDistance = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const distanceMeters = distanceInMeters(coordinates[index - 1], coordinates[index]);
    segments.push(distanceMeters);
    totalDistance += distanceMeters;
  }

  const targetDistance = totalDistance * progress;
  let traversed = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const segmentDistance = segments[index];
    if (traversed + segmentDistance >= targetDistance) {
      // A repeated point makes a zero-length segment, and 0/0 is NaN — which
      // then travels silently into whatever consumes the coordinate.
      const segmentProgress = segmentDistance ? (targetDistance - traversed) / segmentDistance : 0;
      const from = coordinates[index];
      const to = coordinates[index + 1];
      return [
        from[0] + (to[0] - from[0]) * segmentProgress,
        from[1] + (to[1] - from[1]) * segmentProgress,
      ];
    }
    traversed += segmentDistance;
  }
  return coordinates[coordinates.length - 1];
}

export function distanceInMeters(from, to) {
  const radians = (degrees) => degrees * Math.PI / 180;
  const lat1 = radians(from[1]);
  const lat2 = radians(to[1]);
  const deltaLat = radians(to[1] - from[1]);
  const deltaLng = radians(to[0] - from[0]);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// A geodesic circle (ring of points at a fixed metric radius) so a drawn
// boundary tracks real ground distance and scales correctly with zoom — a
// pixel-radius circle wouldn't. Shared by the overworld's supply-run-boundary
// preview and the tactical view's zone/interact-range rings (both draw the
// same real-world circle, just at different times).
export function geodesicCircle(center, radiusMeters, steps = 64) {
  const earthRadius = 6371000;
  const lat1 = (center[1] * Math.PI) / 180;
  const lon1 = (center[0] * Math.PI) / 180;
  const angular = radiusMeters / earthRadius;
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const bearing = (2 * Math.PI * i) / steps;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing));
    const lon2 = lon1 + Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
    );
    ring.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return ring;
}

function aStar(graph, startId, endId, options = {}) {
  const blockedEdgeIds = options.blockedEdgeIds || new Set();
  const edgePenaltyIds = options.edgePenaltyIds || new Set();
  const edgePenaltyMeters = Number.isFinite(options.edgePenaltyMeters) ? options.edgePenaltyMeters : 0;
  const open = new Set([startId]);
  const cameFrom = new Map();
  const gScore = new Map([[startId, 0]]);
  const fScore = new Map([[startId, heuristic(graph, startId, endId)]]);

  while (open.size) {
    const current = lowestScore(open, fScore);
    if (current === endId) return reconstructPath(cameFrom, current);
    open.delete(current);

    for (const neighbor of graph.nodes.get(current).neighbors) {
      if (blockedEdgeIds.has(neighbor.edgeId)) continue;
      const penalty = edgePenaltyIds.has(neighbor.edgeId) ? edgePenaltyMeters : 0;
      const tentativeScore = (gScore.get(current) ?? Infinity) + neighbor.distanceMeters + penalty;
      if (tentativeScore >= (gScore.get(neighbor.id) ?? Infinity)) continue;
      cameFrom.set(neighbor.id, current);
      gScore.set(neighbor.id, tentativeScore);
      fScore.set(neighbor.id, tentativeScore + heuristic(graph, neighbor.id, endId));
      open.add(neighbor.id);
    }
  }
  return null;
}

function heuristic(graph, fromId, toId) {
  return distanceInMeters(graph.nodes.get(fromId).coordinate, graph.nodes.get(toId).coordinate);
}

function lowestScore(open, scores) {
  let lowest = null;
  for (const id of open) {
    if (lowest === null || (scores.get(id) ?? Infinity) < (scores.get(lowest) ?? Infinity)) {
      lowest = id;
    }
  }
  return lowest;
}

function reconstructPath(cameFrom, current) {
  const path = [current];
  while (cameFrom.has(current)) {
    current = cameFrom.get(current);
    path.unshift(current);
  }
  return path;
}

function polylineDistance(coordinates) {
  let distanceMeters = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    distanceMeters += distanceInMeters(coordinates[index - 1], coordinates[index]);
  }
  return distanceMeters;
}

function removeAdjacentDuplicates(coordinates) {
  return coordinates.filter((coordinate, index) => {
    if (!index) return true;
    const previous = coordinates[index - 1];
    return coordinate[0] !== previous[0] || coordinate[1] !== previous[1];
  });
}

function removeAdjacentValues(values) {
  return values.filter((value, index) => !index || value !== values[index - 1]);
}

function roadblockSlotForCorridorEnd(corridor, endNodeId, offsetMeters) {
  const fromStart = endNodeId === corridor.startNodeId;
  const fromEnd = endNodeId === corridor.endNodeId;
  if (!fromStart && !fromEnd) return null;
  const coordinates = fromStart ? corridor.coordinates : [...corridor.coordinates].reverse();
  return {
    corridorId: corridor.id,
    endNodeId,
    coordinate: coordinateAtDistance(coordinates, offsetMeters),
    offsetMeters,
  };
}

function coordinateAtDistance(coordinates, targetMeters) {
  if (!coordinates.length) return null;
  if (coordinates.length === 1 || targetMeters <= 0) return coordinates[0];
  let traversed = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const from = coordinates[index - 1];
    const to = coordinates[index];
    const segmentMeters = distanceInMeters(from, to);
    if (traversed + segmentMeters >= targetMeters) {
      const ratio = segmentMeters ? (targetMeters - traversed) / segmentMeters : 0;
      return [
        from[0] + (to[0] - from[0]) * ratio,
        from[1] + (to[1] - from[1]) * ratio,
      ];
    }
    traversed += segmentMeters;
  }
  return coordinates.at(-1);
}

function edgeKey(fromId, toId) {
  return String(fromId) < String(toId) ? `${fromId}:${toId}` : `${toId}:${fromId}`;
}

function addCorridor(graph, corridors, corridorByEdge, nodeIds, edgeIds) {
  if (!edgeIds.length) return;
  // Corridor references enter durable intelligence and saves, so identity
  // cannot depend on the order Overpass ways happen to be processed.
  const id = `corridor:${[...edgeIds].sort().join(",")}`;
  const corridor = {
    id,
    startNodeId: nodeIds[0],
    endNodeId: nodeIds.at(-1),
    nodeIds,
    edgeIds,
    coordinates: nodeIds.map((nodeId) => graph.nodes.get(nodeId).coordinate),
    lengthMeters: edgeIds.reduce(
      (total, edgeId) => total + (graph.edges.get(edgeId)?.distanceMeters || 0),
      0,
    ),
    neighborIds: [],
  };
  corridors.set(id, corridor);
  for (const edgeId of edgeIds) corridorByEdge.set(edgeId, id);
}
